import PropTypes from 'prop-types';
import {cnb} from 'cnbuilder';
import {useRef, useEffect, useState, useCallback} from 'react';
import HorizontalRule from '@teladoc/pulse/ui/HorizontalRule';
import KeyHandler from '@teladoc/fe-ccm/ui/common/utilities/key-handler';
import {NAV_MENU} from '@/config';
import NavLink from '../NavLink';
import parentClass from '../Navigation.scss';
import css from './SubNavigation.scss';

const SubNavigation = ({
    className,
    itemClassName,
    activeItemClassName,
    onItemClick,
    activeRoute,
    tabName,
}) => {
    const expandMenu = useRef(null);
    const [needScroll, setNeedScroll] = useState(false);

    const checkMenuHeight = useCallback(() => {
        if (!expandMenu.current) return;

        if (expandMenu.current) {
            const menuHeight = expandMenu.current.clientHeight;
            // Subtracting 1/2 of the menu height from the window height to allow some space
            const windowHeight = window.innerHeight - menuHeight / 2;

            setNeedScroll(menuHeight > windowHeight);
        }
    }, []);

    useEffect(() => {
        checkMenuHeight();
        window.addEventListener('resize', checkMenuHeight);

        return () => {
            window.removeEventListener('resize', checkMenuHeight);
        };
    }, [checkMenuHeight]);

    return (
        <ul
            ref={expandMenu}
            className={cnb(css.container, parentClass.root, className, {
                [css.scroll]: needScroll,
            })}
            role="menu"
        >
            {activeRoute.map(
                ({
                    id,
                    path,
                    content,
                    isHorizontal,
                    mixpanelEvent,
                    mixpanelProperty,
                    amplitudeProperty,
                    icon: Icon,
                    testAttr,
                }) => (
                    <li key={id} role="none">
                        <NavLink
                            id={id}
                            path={path}
                            Icon={Icon}
                            content={content}
                            ariaRole="menuitem"
                            onItemClick={onItemClick}
                            mixpanelObj={{
                                event: mixpanelEvent,
                                property: mixpanelProperty,
                            }}
                            amplitudeProperty={amplitudeProperty}
                            itemClassName={cnb(itemClassName)}
                            activeItemClassName={activeItemClassName}
                            iconClassName={cnb(
                                {
                                    [parentClass.navColorIcon]:
                                        tabName === NAV_MENU.Programs.id,
                                    [parentClass.navProgramIcon]:
                                        tabName !== NAV_MENU.Programs.id,
                                },
                                {[parentClass.navContentIcon]: content}
                            )}
                            name={`menu-item-${id}`}
                            onKeyDown={e =>
                                KeyHandler.keyDown(e, expandMenu?.current, true)
                            }
                            testAttr={testAttr}
                        />
                        {isHorizontal && activeRoute?.length > 1 && (
                            <HorizontalRule />
                        )}
                    </li>
                )
            )}
        </ul>
    );
};

SubNavigation.propTypes = {
    className: PropTypes.string,
    onItemClick: PropTypes.func,
    itemClassName: PropTypes.string,
    activeItemClassName: PropTypes.string,
    activeRoute: PropTypes.array,
    tabName: PropTypes.string,
};

export default SubNavigation;
